// Firebase Subcollections Structure
import { collection, doc, setDoc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit, writeBatch, runTransaction, increment } from 'firebase/firestore';
import { db } from './config';

// Utility function to generate unique IDs
const generateId = () => {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
};

// ===== USER SUBCOLLECTIONS =====

// User Test Results Subcollection
export const saveUserTestResult = async (userId, testResultData) => {
  try {
    const docRef = await addDoc(collection(db, 'users', userId, 'testResults'), {
      ...testResultData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserTestResults = async (userId, limitCount = 10) => {
  try {
    const q = query(
      collection(db, 'users', userId, 'testResults'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const results = [];
    querySnapshot.forEach((doc) => {
      results.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: results };
  } catch (error) {
    return { success: false, error };
  }
};

// User Notes Subcollection
export const saveUserNote = async (userId, noteData) => {
  try {
    const docRef = await addDoc(collection(db, 'users', userId, 'notes'), {
      ...noteData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserNotes = async (userId, limitCount = 100) => {
  try {
    const q = query(
      collection(db, 'users', userId, 'notes'),
      orderBy('updatedAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const notes = [];
    querySnapshot.forEach((doc) => {
      notes.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: notes };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateUserNote = async (userId, noteId, updateData) => {
  try {
    const docRef = doc(db, 'users', userId, 'notes', noteId);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteUserNote = async (userId, noteId) => {
  try {
    await deleteDoc(doc(db, 'users', userId, 'notes', noteId));
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// User Doubts Subcollection
export const saveUserDoubt = async (userId, doubtData) => {
  try {
    const docRef = await addDoc(collection(db, 'users', userId, 'doubts'), {
      ...doubtData,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: 'pending'
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserDoubts = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'users', userId, 'doubts'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const doubts = [];
    querySnapshot.forEach((doc) => {
      doubts.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: doubts };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateUserDoubt = async (userId, doubtId, updateData) => {
  try {
    const docRef = doc(db, 'users', userId, 'doubts', doubtId);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// User Progress Subcollection
export const saveUserProgress = async (userId, progressData) => {
  try {
    const docRef = await addDoc(collection(db, 'users', userId, 'progress'), {
      ...progressData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserProgress = async (userId, limit = 50) => {
  try {
    const q = query(
      collection(db, 'users', userId, 'progress'),
      orderBy('updatedAt', 'desc'),
      limit(limit)
    );
    const querySnapshot = await getDocs(q);
    const progress = [];
    querySnapshot.forEach((doc) => {
      progress.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: progress };
  } catch (error) {
    return { success: false, error };
  }
};

// User Activities Subcollection
export const saveUserActivity = async (userId, activityData) => {
  try {
    const docRef = await addDoc(collection(db, 'users', userId, 'activities'), {
      ...activityData,
      timestamp: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserActivities = async (userId, limit = 100) => {
  try {
    const q = query(
      collection(db, 'users', userId, 'activities'),
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    const querySnapshot = await getDocs(q);
    const activities = [];
    querySnapshot.forEach((doc) => {
      activities.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: activities };
  } catch (error) {
    return { success: false, error };
  }
};

// User Settings Subcollection
export const saveUserSettings = async (userId, settingsData) => {
  try {
    const docRef = doc(db, 'users', userId, 'settings', 'preferences');
    await setDoc(docRef, {
      ...settingsData,
      updatedAt: new Date()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserSettings = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId, 'settings', 'preferences');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: true, data: null };
    }
  } catch (error) {
    return { success: false, error };
  }
};

// ===== COURSE SUBCOLLECTIONS =====

// Course Enrollments Subcollection
export const enrollUserInCourse = async (userId, courseId, enrollmentData = {}) => {
  try {
    const enrollmentRef = doc(db, 'users', userId, 'enrollments', courseId);
    const enrollment = {
      courseId,
      enrolledAt: new Date(),
      status: 'active',
      progress: 0,
      completedLessons: 0,
      totalLessons: enrollmentData.totalLessons || 0,
      lastAccessed: new Date(),
      ...enrollmentData
    };
    
    await setDoc(enrollmentRef, enrollment);
    return { success: true, data: enrollment };
  } catch (error) {
    return { success: false, error };
  }
};

export const getUserEnrollments = async (userId) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users', userId, 'enrollments'));
    const enrollments = [];
    querySnapshot.forEach((doc) => {
      enrollments.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: enrollments };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateEnrollmentProgress = async (userId, courseId, progressData) => {
  try {
    const enrollmentRef = doc(db, 'users', userId, 'enrollments', courseId);
    const updateData = {
      ...progressData,
      lastAccessed: new Date()
    };
    
    await updateDoc(enrollmentRef, updateData);
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Assignments Subcollection
export const saveCourseAssignment = async (courseId, assignmentData) => {
  try {
    const docRef = await addDoc(collection(db, 'courses', courseId, 'assignments'), {
      ...assignmentData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getCourseAssignments = async (courseId) => {
  try {
    const q = query(
      collection(db, 'courses', courseId, 'assignments'),
      orderBy('dueDate', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const assignments = [];
    querySnapshot.forEach((doc) => {
      assignments.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: assignments };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Materials Subcollection
export const saveCourseMaterial = async (courseId, materialData) => {
  try {
    const docRef = await addDoc(collection(db, 'courses', courseId, 'materials'), {
      ...materialData,
      createdAt: new Date(),
      downloadCount: 0
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getCourseMaterials = async (courseId) => {
  try {
    const q = query(
      collection(db, 'courses', courseId, 'materials'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const materials = [];
    querySnapshot.forEach((doc) => {
      materials.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: materials };
  } catch (error) {
    return { success: false, error };
  }
};

// Course Discussions Subcollection
export const saveCourseDiscussion = async (courseId, discussionData) => {
  try {
    const docRef = await addDoc(collection(db, 'courses', courseId, 'discussions'), {
      ...discussionData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getCourseDiscussions = async (courseId) => {
  try {
    const q = query(
      collection(db, 'courses', courseId, 'discussions'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const discussions = [];
    querySnapshot.forEach((doc) => {
      discussions.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: discussions };
  } catch (error) {
    return { success: false, error };
  }
};

// ===== DOUBT SUBCOLLECTIONS =====

// Doubt Responses Subcollection
export const addDoubtResponse = async (doubtId, responseData) => {
  try {
    const docRef = await addDoc(collection(db, 'doubts', doubtId, 'responses'), {
      ...responseData,
      createdAt: new Date(),
      isAccepted: false,
      likeCount: 0,
      isExpert: false
    });
    
    // Update doubt response count
    const doubtRef = doc(db, 'doubts', doubtId);
    await updateDoc(doubtRef, {
      responseCount: responseData.responseCount + 1,
      updatedAt: new Date()
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const getDoubtResponses = async (doubtId, limit = 10) => {
  try {
    const q = query(
      collection(db, 'doubts', doubtId, 'responses'),
      orderBy('createdAt', 'asc'),
      limit(limit)
    );
    const querySnapshot = await getDocs(q);
    const responses = [];
    querySnapshot.forEach((doc) => {
      responses.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: responses };
  } catch (error) {
    return { success: false, error };
  }
};

// ===== SESSION SUBCOLLECTIONS =====

// Session Registrations Subcollection
export const registerForSession = async (sessionId, userId) => {
  try {
    const registrationRef = doc(db, 'liveSessions', sessionId, 'registrations', userId);
    
    await setDoc(registrationRef, {
      userId,
      registeredAt: new Date(),
      status: 'registered'
    });
    
    // Update session participant count
    const sessionRef = doc(db, 'liveSessions', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();
      await updateDoc(sessionRef, {
        participantCount: sessionData.participantCount + 1,
        updatedAt: new Date()
      });
    }
    
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getSessionRegistrations = async (sessionId) => {
  try {
    const q = query(
      collection(db, 'liveSessions', sessionId, 'registrations'),
      orderBy('registeredAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const registrations = [];
    querySnapshot.forEach((doc) => {
      registrations.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: registrations };
  } catch (error) {
    return { success: false, error };
  }
};

// ===== DAILY TODO LIST SUBCOLLECTION =====
export const saveDailyTodo = async (userId, todoData) => {
  try {
    const docRef = await addDoc(collection(db, 'users', userId, 'dailyTodos'), {
      ...todoData,
      createdAt: new Date(),
      completed: false,
      // Set expiration to next midnight IST (UTC+5:30)
      expiresAt: getNextMidnightIST()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateDailyTodo = async (userId, todoId, updates) => {
  try {
    const docRef = doc(db, 'users', userId, 'dailyTodos', todoId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const deleteDailyTodo = async (userId, todoId) => {
  try {
    const docRef = doc(db, 'users', userId, 'dailyTodos', todoId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const getDailyTodos = async (userId) => {
  try {
    const q = query(
      collection(db, 'users', userId, 'dailyTodos'),
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const todos = [];
    querySnapshot.forEach((doc) => {
      todos.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: todos };
  } catch (error) {
    return { success: false, error };
  }
};

// ===== MOTIVATIONAL QUOTES =====
export const getRandomMotivationalQuote = async () => {
  try {
    const quotes = [
      "Success is not final, failure is not fatal: it is the courage to continue that counts. - Winston Churchill",
      "The only way to do great work is to love what you do. - Steve Jobs",
      "Believe you can and you're halfway there. - Theodore Roosevelt",
      "The future belongs to those who believe in the beauty of their dreams. - Eleanor Roosevelt",
      "Don't watch the clock; do what it does. Keep going. - Sam Levenson",
      "The only limit to our realization of tomorrow is our doubts of today. - Franklin D. Roosevelt",
      "Success is walking from failure to failure with no loss of enthusiasm. - Winston Churchill",
      "The way to get started is to quit talking and begin doing. - Walt Disney",
      "It does not matter how slowly you go as long as you do not stop. - Confucius",
      "The only person you are destined to become is the person you decide to be. - Ralph Waldo Emerson",
      "Your time is limited, don't waste it living someone else's life. - Steve Jobs",
      "The mind is everything. What you think you become. - Buddha",
      "The best way to predict the future is to create it. - Peter Drucker",
      "Don't let yesterday take up too much of today. - Will Rogers",
      "The journey of a thousand miles begins with one step. - Lao Tzu",
      "What you get by achieving your goals is not as important as what you become by achieving your goals. - Zig Ziglar",
      "The only impossible journey is the one you never begin. - Tony Robbins",
      "Success usually comes to those who are too busy to be looking for it. - Henry David Thoreau",
      "The harder you work for something, the greater you'll feel when you achieve it. - Unknown",
      "Dream big and dare to fail. - Norman Vaughan",
      "The difference between ordinary and extraordinary is that little extra. - Jimmy Johnson",
      "The only way to achieve the impossible is to believe it is possible. - Charles Kingsleigh",
      "Your attitude, not your aptitude, will determine your altitude. - Zig Ziglar",
      "The best revenge is massive success. - Frank Sinatra",
      "I find that the harder I work, the more luck I seem to have. - Thomas Jefferson",
      "The only place where success comes before work is in the dictionary. - Vidal Sassoon",
      "Success is not the key to happiness. Happiness is the key to success. - Albert Schweitzer",
      "The road to success and the road to failure are almost exactly the same. - Colin R. Davis",
      "Success is getting what you want, happiness is wanting what you get. - W.P. Kinsella"
    ];
    
    const randomIndex = Math.floor(Math.random() * quotes.length);
    return { success: true, data: quotes[randomIndex] };
  } catch (error) {
    return { success: false, error };
  }
};

// Helper function to get next midnight IST
const getNextMidnightIST = () => {
  const now = new Date();
  // IST is UTC+5:30
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(now.getTime() + istOffset);
  
  // Create a new date for the next midnight IST
  const nextMidnightIST = new Date(istTime);
  nextMidnightIST.setUTCHours(0, 0, 0, 0); // Set to midnight (00:00:00)
  nextMidnightIST.setUTCDate(nextMidnightIST.getUTCDate() + 1); // Add one day to get next midnight
  
  // Convert back to UTC for storage
  return new Date(nextMidnightIST.getTime() - istOffset);
};

// ===== COMPREHENSIVE USER DATA FUNCTIONS =====

// Get all user data from subcollections
export const getUserDashboardData = async (userId) => {
  try {
    const [
      testResultsResult,
      notesResult,
      doubtsResult,
      progressResult,
      activitiesResult,
      enrollmentsResult,
      settingsResult
    ] = await Promise.all([
      getUserTestResults(userId, 5),
      getUserNotes(userId, 10),
      getUserDoubts(userId, 5),
      getUserProgress(userId, 10),
      getUserActivities(userId, 20),
      getUserEnrollments(userId),
      getUserSettings(userId)
    ]);

    // Calculate dashboard stats
    const testResults = testResultsResult.success ? testResultsResult.data : [];
    const activities = activitiesResult.success ? activitiesResult.data : [];
    const enrollments = enrollmentsResult.success ? enrollmentsResult.data : [];

    const stats = {
      totalTests: testResults.length,
      totalCourses: enrollments.length,
      averageAccuracy: testResults.length > 0 
        ? Math.round(testResults.reduce((sum, test) => sum + (test.accuracy || 0), 0) / testResults.length)
        : 0,
      studyStreak: calculateStudyStreak(activities),
      totalNotes: notesResult.success ? notesResult.data.length : 0,
      totalDoubts: doubtsResult.success ? doubtsResult.data.length : 0
    };

    const dashboardData = {
      stats,
      recentTestResults: testResults.slice(0, 3),
      recentNotes: notesResult.success ? notesResult.data.slice(0, 3) : [],
      recentDoubts: doubtsResult.success ? doubtsResult.data.slice(0, 3) : [],
      recentActivities: activities.slice(0, 5),
      enrollments,
      settings: settingsResult.success ? settingsResult.data : null,
      lastUpdated: new Date()
    };

    return { success: true, data: dashboardData };
  } catch (error) {
    return { success: false, error };
  }
};

// Helper function to calculate study streak (legacy - use StudyStreakManager for new implementations)
const calculateStudyStreak = (activities) => {
  if (!activities || activities.length === 0) return 0;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Convert to IST
  const istToday = new Date(today.getTime() + (5.5 * 60 * 60 * 1000));
  istToday.setHours(0, 0, 0, 0);
  
  let streak = 0;
  let currentDate = new Date(istToday);
  
  while (true) {
    const hasActivity = activities.some(activity => {
      let activityDate;
      
      if (activity.timestamp) {
        if (activity.timestamp.toDate) {
          activityDate = activity.timestamp.toDate();
        } else {
          activityDate = new Date(activity.timestamp);
        }
      } else if (activity.createdAt) {
        if (activity.createdAt.toDate) {
          activityDate = activity.createdAt.toDate();
        } else {
          activityDate = new Date(activity.createdAt);
        }
      } else if (activity.date) {
        activityDate = new Date(activity.date);
      } else {
        return false;
      }
      
      // Convert to IST
      const istActivityDate = new Date(activityDate.getTime() + (5.5 * 60 * 60 * 1000));
      istActivityDate.setHours(0, 0, 0, 0);
      
      return istActivityDate.getTime() === currentDate.getTime();
    });
    
    if (hasActivity) {
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      break;
    }
  }
  
  return streak;
};

// ===== MIGRATION FUNCTIONS =====

// Migrate user data from old collections to subcollections
export const migrateUserDataToSubcollections = async (userId) => {
  try {
    // Get data from old collections
    const [
      oldTestResults,
      oldNotes,
      oldDoubts,
      oldProgress,
      oldActivities,
      oldCourseData
    ] = await Promise.all([
      // These would be calls to your old collection functions
      // For now, we'll assume they return empty arrays
      Promise.resolve({ success: true, data: [] }),
      Promise.resolve({ success: true, data: [] }),
      Promise.resolve({ success: true, data: [] }),
      Promise.resolve({ success: true, data: [] }),
      Promise.resolve({ success: true, data: [] }),
      Promise.resolve({ success: true, data: null })
    ]);

    const batch = writeBatch(db);
    let migrationCount = 0;

    // Migrate test results
    if (oldTestResults.success && oldTestResults.data.length > 0) {
      for (const testResult of oldTestResults.data) {
        const docRef = doc(db, 'users', userId, 'testResults', testResult.id || generateId());
        batch.set(docRef, testResult);
        migrationCount++;
      }
    }

    // Migrate notes
    if (oldNotes.success && oldNotes.data.length > 0) {
      for (const note of oldNotes.data) {
        const docRef = doc(db, 'users', userId, 'notes', note.id || generateId());
        batch.set(docRef, note);
        migrationCount++;
      }
    }

    // Migrate doubts
    if (oldDoubts.success && oldDoubts.data.length > 0) {
      for (const doubt of oldDoubts.data) {
        const docRef = doc(db, 'users', userId, 'doubts', doubt.id || generateId());
        batch.set(docRef, doubt);
        migrationCount++;
      }
    }

    // Migrate progress
    if (oldProgress.success && oldProgress.data.length > 0) {
      for (const progress of oldProgress.data) {
        const docRef = doc(db, 'users', userId, 'progress', progress.id || generateId());
        batch.set(docRef, progress);
        migrationCount++;
      }
    }

    // Migrate activities
    if (oldActivities.success && oldActivities.data.length > 0) {
      for (const activity of oldActivities.data) {
        const docRef = doc(db, 'users', userId, 'activities', activity.id || generateId());
        batch.set(docRef, activity);
        migrationCount++;
      }
    }

    // Commit the batch
    if (migrationCount > 0) {
      await batch.commit();
      }

    return { success: true, migratedCount: migrationCount };
  } catch (error) {
    return { success: false, error };
  }
};

// Initialize user subcollections with default data
export const initializeUserSubcollections = async (userId) => {
  try {
    const batch = writeBatch(db);

    // Initialize user settings
    const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
    batch.set(settingsRef, {
      theme: 'light',
      notifications: true,
      language: 'en',
      createdAt: new Date(),
      updatedAt: new Date()
    });

    // Initialize user profile if it doesn't exist
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      batch.set(userRef, {
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }

    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};
